import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Unpublished as UnpublishedIcon,
  Memory as MemoryIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchNamespaceHealth,
  checkMemoryIntegrity,
  rebuildMemory,
} from "../../../redux/actions/assetActions";
import { clearNamespaceHealth } from "../../../redux/reducers/assetReducer";
import { AssetHealthStatus } from "../../../types/memoryHealth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MemoryHealthView: React.FC = () => {
  const { namespace } = useParams<{ namespace: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const { memoryHealth } = useSelector((state: RootState) => state.assets);
  const { 
    namespaceHealth, 
    integrityCheck,
    namespaceHealthLoading, 
    integrityCheckLoading,
    error 
  } = memoryHealth;

  useEffect(() => {
    if (namespace) {
      // Clear previous data to prevent stale state
      dispatch(clearNamespaceHealth());
      
      // Fetch data in parallel - now safe with separate loading states
      dispatch(fetchNamespaceHealth(namespace));
      dispatch(checkMemoryIntegrity(namespace));
    }
  }, [dispatch, namespace]);

  const handleRefresh = () => {
    if (namespace) {
      // Clear previous data
      dispatch(clearNamespaceHealth());
      
      // Fetch data in parallel - now safe with separate loading states
      dispatch(fetchNamespaceHealth(namespace));
      dispatch(checkMemoryIntegrity(namespace));
    }
  };

  const handleRebuild = async () => {
    if (!namespace) return;

    setIsRebuilding(true);
    try {
      const result = await dispatch(
        rebuildMemory({
          namespace,
          assetIds: undefined,
          rebuildAll: true,
        })
      ).unwrap();

      toast.success(result.message || "Memory rebuild initiated successfully");
      setRebuildDialogOpen(false);

      // Refresh the data
      setTimeout(() => {
        handleRefresh();
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "Failed to initiate memory rebuild");
    } finally {
      setIsRebuilding(false);
    }
  };

  const getStatusIcon = (status: AssetHealthStatus) => {
    switch (status) {
      case AssetHealthStatus.HEALTHY:
        return <CheckCircleIcon sx={{ color: "#48bb78", fontSize: 20 }} />;
      case AssetHealthStatus.PROCESSING:
        return <CircularProgress size={16} sx={{ color: "#3182ce" }} />;
      case AssetHealthStatus.VERSION_MISMATCH:
        return <WarningIcon sx={{ color: "#f59e0b", fontSize: 20 }} />;
      case AssetHealthStatus.MISSING_IN_MEMORY:
        return <ErrorIcon sx={{ color: "#ef4444", fontSize: 20 }} />;
      case AssetHealthStatus.NEEDS_REBUILD:
        return <WarningIcon sx={{ color: "#ed8936", fontSize: 20 }} />;
      case AssetHealthStatus.NOT_PUBLISHED:
        return <UnpublishedIcon sx={{ color: "#9ca3af", fontSize: 20 }} />;
      case AssetHealthStatus.DELETED:
        return <DeleteIcon sx={{ color: "#6b7280", fontSize: 20 }} />;
      case AssetHealthStatus.CRASHED:
        return <ErrorIcon sx={{ color: "#dc2626", fontSize: 20 }} />;
      case AssetHealthStatus.SCHEMA_OUTDATED:
        return <WarningIcon sx={{ color: "#ea580c", fontSize: 20 }} />;
      case AssetHealthStatus.ERROR:
        return <ErrorIcon sx={{ color: "#f56565", fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: AssetHealthStatus, crashDetails?: any) => {
    const colors: Record<AssetHealthStatus, { bg: string; text: string }> = {
      [AssetHealthStatus.HEALTHY]: { bg: "#c6f6d5", text: "#22543d" },
      [AssetHealthStatus.PROCESSING]: { bg: "#dbeafe", text: "#1e3a8a" },
      [AssetHealthStatus.VERSION_MISMATCH]: { bg: "#fef3c7", text: "#78350f" },
      [AssetHealthStatus.MISSING_IN_MEMORY]: { bg: "#fee2e2", text: "#7f1d1d" },
      [AssetHealthStatus.NEEDS_REBUILD]: { bg: "#fed7aa", text: "#7c2d12" },
      [AssetHealthStatus.NOT_PUBLISHED]: { bg: "#e5e7eb", text: "#374151" },
      [AssetHealthStatus.DELETED]: { bg: "#d1d5db", text: "#1f2937" },
      [AssetHealthStatus.CRASHED]: { bg: "#fecaca", text: "#991b1b" },
      [AssetHealthStatus.SCHEMA_OUTDATED]: { bg: "#fed7aa", text: "#92400e" },
      [AssetHealthStatus.ERROR]: { bg: "#fed7d7", text: "#742a2a" },
    };

    const color = colors[status] || { bg: "#f3f4f6", text: "#6b7280" };
    const icon = getStatusIcon(status);

    const chip = (
      <Chip
        icon={icon || undefined}
        label={status.replace("_", " ").toUpperCase()}
        size="small"
        sx={{
          backgroundColor: color.bg,
          color: color.text,
          fontWeight: 500,
          "& .MuiChip-icon": {
            color: "inherit",
          },
        }}
      />
    );

    // If crashed with details, wrap in tooltip
    if (status === AssetHealthStatus.CRASHED && crashDetails) {
      return (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Crash Details:
              </Typography>
              <Typography variant="caption" display="block">
                Transaction Index: {crashDetails.transaction_index}
              </Typography>
              <Typography variant="caption" display="block">
                Type: {crashDetails.type}
              </Typography>
              <Typography variant="caption" display="block">
                Major Version: {crashDetails.major_version}
              </Typography>
            </Box>
          }
          arrow
        >
          {chip}
        </Tooltip>
      );
    }

    return chip;
  };

  // Show loading if either operation is loading and we don't have data yet
  const isLoading = (namespaceHealthLoading || integrityCheckLoading) && !namespaceHealth;
  
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Stack spacing={2}>
            {/* Navigation and Title */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  onClick={() => navigate("/dashboard/memory-health")}
                  sx={{
                    backgroundColor: "#f7fafc",
                    "&:hover": { backgroundColor: "#edf2f7" },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MemoryIcon sx={{ color: "#4299e1", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#2d3748",
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    Memory Health: {namespace}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<BuildIcon />}
                  onClick={() => setRebuildDialogOpen(true)}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#4299e1",
                    "&:hover": { backgroundColor: "#3182ce" },
                  }}
                >
                  Rebuild Namespace
                </Button>

                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      backgroundColor: "#f7fafc",
                      "&:hover": { backgroundColor: "#edf2f7" },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* Integrity Check Alert - Only show crash alert if there are actual issues */}
            {integrityCheck?.has_crashes &&
              namespaceHealth &&
              namespaceHealth.needs_rebuild_count > 0 && (
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    "& .MuiAlert-icon": {
                      color: "#991b1b",
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Memory Crash Detected
                  </Typography>
                  <Typography variant="body2">
                    The memory system has crashed and needs to be rebuilt.
                    {integrityCheck.crash_details &&
                      ` Transaction index: ${integrityCheck.crash_details.transaction_index}`}
                  </Typography>
                </Alert>
              )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Box>
      </Box>

      {/* Assets Table - Only show actionable items */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3, backgroundColor: "#f7fafc" }}>
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Asset ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">DB Version</TableCell>
                <TableCell align="center">Published Ver</TableCell>
                <TableCell align="center">Schema Ver</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {namespaceHealth?.assets
                .filter(
                  (asset) =>
                    // Only show actionable items by default
                    asset.status !== AssetHealthStatus.DELETED
                )
                .sort((a, b) => {
                  // Sort by severity: CRASHED > SCHEMA_OUTDATED > ERROR > MISSING > NEEDS_REBUILD > VERSION_MISMATCH > PROCESSING
                  const severityOrder = {
                    [AssetHealthStatus.CRASHED]: 0,
                    [AssetHealthStatus.SCHEMA_OUTDATED]: 1,
                    [AssetHealthStatus.ERROR]: 2,
                    [AssetHealthStatus.MISSING_IN_MEMORY]: 3,
                    [AssetHealthStatus.NEEDS_REBUILD]: 4,
                    [AssetHealthStatus.VERSION_MISMATCH]: 5,
                    [AssetHealthStatus.PROCESSING]: 6,
                    [AssetHealthStatus.HEALTHY]: 7,
                    [AssetHealthStatus.NOT_PUBLISHED]: 8,
                    [AssetHealthStatus.DELETED]: 9,
                  };
                  return (
                    (severityOrder[a.status] ?? 10) -
                    (severityOrder[b.status] ?? 10)
                  );
                })
                .map((asset) => (
                  <TableRow key={asset.asset_id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                        }}
                      >
                        {asset.asset_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(asset.status, asset.crash_details)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {asset.db_version}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {asset.published_version}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {asset.schema_version !== null
                          ? asset.schema_version
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {format(new Date(asset.last_modified), "MMM d, HH:mm")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/dashboard/assets/${namespace}/${asset.asset_id}`
                            )
                          }
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Rebuild Dialog */}
      <Dialog
        open={rebuildDialogOpen}
        onClose={() => !isRebuilding && setRebuildDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rebuild Namespace Memory</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "#718096" }}>
              This will rebuild the memory index for all assets in the namespace
              "{namespace}".
            </Typography>

            <Alert severity="warning">
              This operation will trigger a background task to rebuild the
              memory index. It may take several minutes to complete depending on
              the number of assets.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRebuildDialogOpen(false)}
            disabled={isRebuilding}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRebuild}
            variant="contained"
            disabled={isRebuilding}
            startIcon={
              isRebuilding ? <CircularProgress size={16} /> : <BuildIcon />
            }
          >
            {isRebuilding ? "Rebuilding..." : "Start Rebuild"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoryHealthView;
